import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { SP_BREAKPOINT } from "../../../constants/app";
import { PRIMARY_DARK_GREEN } from "../../../constants/color";
import { FileUploadResult, fileActions } from "../../../ducks/file";
import { TableSortBy } from "../../../hooks/useTable";
import { AppDispatch } from "../../../store";
import { getFileSizeInMB } from "../../../utils/file";
import { Alert } from "../../atoms/Alert";
import { Select } from "../../atoms/Select";
import { SearchIcon } from "../../atoms/icons/SearchIcon";
import { FileResult } from "../../molecules/file_upload/FileResult";
import { FileResultSP } from "../../molecules/file_upload/FileResultSP";
import { FileUpload } from "../../molecules/file_upload/FileUpload";
import { MainTemplate } from "../../templates/MainTemplate";
import { TableTemplate } from "../../templates/TableTemplate";
import { useFileUpload } from "./hook";

export const FileUploadPage: React.FC = () => {
  const {
    pageTitle,
    tableHeaders,
    templateOptions,
    file,
    results,
    total,
    setFile,
    selectedTemplate,
    fetchFileUploadResult,
    handleFileUpload,
    handleOnCancel,
    handleOnSearch,
    handleOnSelectTemplate,
    handleRowDownload,
    handleRowDelete,
    handleBulkDelete,
    handleSelectAll,
    handleRowSelect,
    isUploading,
    hasError,
    setHasError,
    checkFileExtension,
  } = useFileUpload();

  const dispatch = useDispatch<AppDispatch>();
  const { menuId } = useParams<{ menuId: string }>();
  const [currentMenuId, setCurrentMenuId] = useState(menuId);
  const [isLoading, setIsLoading] = useState(false);
  const defaultSort: TableSortBy = { field: "upload_datetime", order: "desc" };

  useEffect(() => {
    // initialize
    dispatch(fileActions.resetToInitialState());
  }, []);

  useEffect(() => {
    // メニューIDが変更された場合、ファイルアップロード結果を再取得
    if (menuId !== currentMenuId) {
      setIsLoading(true);
      // initialize
      dispatch(fileActions.resetToInitialState());
      setFile(null);
      setHasError(false);
      // ファイルアップロードテンプレートを再取得
      if (menuId) {
        dispatch(
          fileActions.getFileUploadTemplates.started({
            menu_id: Number(menuId),
            onComplete: () => setIsLoading(false),
          }),
        );
      }
      // ファイルアップロード結果を再取得
      fetchFileUploadResult(defaultSort);
      setCurrentMenuId(menuId);
    }
  }, [menuId]);

  return (
    <MainTemplate title={`ファイルアップロード　${pageTitle}`} useStickyTitle>
      <StyledContainer>
        <StyledHeader>
          <StyledTemplateTitle>
            データ取り込みテンプレートを選択してください
          </StyledTemplateTitle>

          {!isLoading && templateOptions?.length > 0 && (
            <Select
              name="template"
              options={templateOptions}
              onChange={handleOnSelectTemplate}
              placeholder="選択してください"
              defaultValue={
                templateOptions.length === 1 ? templateOptions[0].name : ""
              }
            />
          )}
          {(isLoading || templateOptions?.length === 0) && (
            <Select
              name="loading_template"
              options={[]}
              onChange={() => {}}
              placeholder="選択してください"
            />
          )}
        </StyledHeader>

        {!file && (
          <FileUpload
            onFileUpload={(file) => {
              checkFileExtension(file, "excel");
            }}
            accept=".xlsx,.xls"
          />
        )}

        {file && (
          <>
            <FileResult
              fileName={file.name}
              fileSize={getFileSizeInMB(file).toFixed(2) + "MB"}
              templateName={selectedTemplate ? selectedTemplate.name : ""}
              isUploading={isUploading}
              onCancel={handleOnCancel}
              onUpload={handleFileUpload}
            />
            <FileResultSP
              fileName={file.name}
              fileSize={getFileSizeInMB(file).toFixed(2) + "MB"}
              templateName={selectedTemplate ? selectedTemplate.name : ""}
              isUploading={isUploading}
              onCancel={handleOnCancel}
              onUpload={handleFileUpload}
            />
          </>
        )}

        {hasError && <Alert />}

        <TableTemplate<FileUploadResult>
          title="アップロード結果"
          number={`（${results.length}件）`}
          button={{
            text: "結果一覧で検索",
            variant: "dark",
            icon: SearchIcon,
            onClick: handleOnSearch,
          }}
          defaultSort={defaultSort}
          headers={tableHeaders}
          data={results}
          fetchDataFunction={fetchFileUploadResult}
          selectable
          deletable
          downloadable
          enableCSVDownload
          perPage={100}
          total={total}
          hasPagination={results.length > 0}
          onDownload={handleRowDownload}
          onCSVDownload={handleRowDownload}
          onDelete={handleRowDelete}
          onBulkDelete={handleBulkDelete}
          onSelect={handleRowSelect}
          onSelectAll={handleSelectAll}
        />
      </StyledContainer>
    </MainTemplate>
  );
};

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const StyledHeader = styled.div``;

const StyledTemplateTitle = styled.div`
  @media (max-width: ${SP_BREAKPOINT}) {
    font-size: 14px;
  }

  color: ${PRIMARY_DARK_GREEN};
  font-size: 20px;
  font-weight: 700;
  line-height: 150%;
`;
